import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, Eye, Heart, MessageSquare, Tag, ArrowLeft, TrendingUp, Instagram, Facebook, Linkedin, Mail, Users } from 'lucide-react';
import { trackTagView } from '../components/Chatbot';
import LanguagePicker, { useTranslation, t } from '../components/LanguagePicker';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api')) return `${BACKEND_URL}${url}`;
  return url;
};

export default function TagPage() {
  const { tagName } = useParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allTags, setAllTags] = useState([]);
  useTranslation();

  useEffect(() => {
    const decoded = decodeURIComponent(tagName);
    trackTagView(decoded);
    setLoading(true);
    Promise.all([
      axios.get(`${BACKEND_URL}/api/blog/articles?tag=${encodeURIComponent(decoded)}&limit=50`),
      axios.get(`${BACKEND_URL}/api/blog/tags`)
    ]).then(([artRes, tagRes]) => {
      setArticles(artRes.data.articles || []);
      setAllTags(tagRes.data.tags || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [tagName]);

  const decoded = decodeURIComponent(tagName);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <div className="blog-page" data-testid="tag-page">
      <header className="blog-header">
        <div className="blog-header-inner">
          <Link to="/" className="blog-logo">
            <TrendingUp size={28} />
            <div>
              <span className="blog-logo-title">Pakde Heru</span>
              <span className="blog-logo-sub">{t('hero_subtitle')}</span>
            </div>
          </Link>
          <nav className="blog-nav">
            <Link to="/" className="blog-nav-link">{t('nav_blog')}</Link>
            <Link to="/buku" className="blog-nav-link">{t('nav_buku')}</Link>
            <a href="https://wa.me/628118007799?text=Halo%20Pakde%20Heru%2C%20saya%20ingin%20bergabung%20di%20komunitas!" target="_blank" rel="noopener noreferrer" className="blog-nav-link komunitas-link" data-testid="nav-komunitas"><Users size={16} /> {t('nav_komunitas')}</a>
            <div className="blog-social-links">
              <a href="https://www.instagram.com/pakdeheruu/" target="_blank" rel="noopener noreferrer"><Instagram size={18} /></a>
              <a href="https://www.facebook.com/pakdeheruu/" target="_blank" rel="noopener noreferrer"><Facebook size={18} /></a>
              <a href="https://www.linkedin.com/in/herusidik" target="_blank" rel="noopener noreferrer"><Linkedin size={18} /></a>
            </div>
          </nav>
          <LanguagePicker />
        </div>
      </header>

      <div className="tag-page-content">
        <div className="tag-page-inner">
          <Link to="/" className="blog-back-link" data-testid="tag-back-link"><ArrowLeft size={18} /> {t('back_to_blog')}</Link>

          <div className="tag-page-header" data-testid="tag-page-header">
            <Tag size={28} className="tag-page-icon" />
            <h1 className="tag-page-title">{decoded}</h1>
            <p className="tag-page-count">{articles.length} {t('articles_found')}</p>
          </div>

          {loading ? (
            <p style={{ color: '#A1A1AA', textAlign: 'center', padding: '3rem' }}>{t('loading_articles')}</p>
          ) : articles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 2rem', color: '#71717A' }}>
              <Tag size={48} style={{ marginBottom: '1rem', color: '#d9fb06' }} />
              <h3 style={{ color: '#EDEDED', marginBottom: '0.5rem' }}>{t('no_articles')}</h3>
              <p>{t('no_articles_with_tag')} "{decoded}".</p>
            </div>
          ) : (
            <div className="tag-articles-grid">
              {articles.map((article) => (
                <Link to={`/blog/${article.id}`} key={article.id} className="blog-card" data-testid={`tag-article-${article.id}`}>
                  {article.featured_image && (
                    <div className="blog-card-img">
                      <img src={resolveImageUrl(article.featured_image)} alt={article.title} loading="lazy" />
                    </div>
                  )}
                  <div className="blog-card-body">
                    {article.category && <span className="blog-card-cat">{article.category}</span>}
                    <h2 className="blog-card-title">{article.title}</h2>
                    <p className="blog-card-excerpt">{article.excerpt}</p>
                    <div className="blog-card-meta">
                      <span><Calendar size={14} /> {formatDate(article.published_at)}</span>
                      <span><Eye size={14} /> {article.views || 0}</span>
                      <span><Heart size={14} /> {article.likes || 0}</span>
                      <span><MessageSquare size={14} /> {article.comment_count || 0}</span>
                    </div>
                    {article.tags && (
                      <div className="blog-card-tags">
                        {article.tags.slice(0, 4).map((tagItem, i) => (
                          <span key={i} className={`blog-tag ${tagItem.toLowerCase() === decoded.toLowerCase() ? 'blog-tag-active' : ''}`}><Tag size={12} /> {tagItem}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Sidebar: All tags */}
          {allTags.length > 0 && (
            <div className="tag-sidebar" data-testid="tag-sidebar">
              <h3 className="tag-sidebar-title">{t('all_tags')}</h3>
              <div className="tag-sidebar-list">
                {allTags.map((tagItem) => (
                  <Link
                    to={`/tag/${encodeURIComponent(tagItem.name)}`}
                    key={tagItem.name}
                    className={`tag-sidebar-item ${tagItem.name.toLowerCase() === decoded.toLowerCase() ? 'active' : ''}`}
                    data-testid={`sidebar-tag-${tagItem.name}`}
                  >
                    <Tag size={14} /> {tagItem.name} <span className="tag-sidebar-count">{tagItem.count}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <footer className="blog-footer">
        <div className="blog-footer-inner">
          <p>&copy; 2026 {t('footer_copyright')}</p>
        </div>
      </footer>
    </div>
  );
}
